import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Spin } from 'antd';
import {
  SearchOutlined,
  AppstoreOutlined,
  BankOutlined,
  ProjectOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { clientsApi, projectApi } from '../api';
import { userRole } from '../../GetRole.utils';
import './commandPalette.css';

const PAGES = [
  { label: 'Dashboard', sub: 'Overview and things to act on', path: '/', icon: <AppstoreOutlined /> },
  { label: 'Clients', sub: 'All clients', path: '/clients', icon: <BankOutlined /> },
  { label: 'Projects', sub: 'All engagements', path: '/projects', icon: <ProjectOutlined /> },
  { label: 'Resources', sub: 'Utilisation per person across projects', path: '/resources', icon: <TeamOutlined /> },
  { label: 'NDA', sub: 'Non-disclosure agreements', path: '/nda', icon: <FileTextOutlined /> },
  { label: 'MSA', sub: 'Master service agreements', path: '/msa', icon: <FileTextOutlined /> },
  { label: 'SOW', sub: 'Statements of work', path: '/sow', icon: <FileTextOutlined /> },
  { label: 'PO', sub: 'Purchase orders', path: '/po', icon: <FileTextOutlined /> },
  { label: 'CR', sub: 'Change requests', path: '/cr', icon: <FileTextOutlined /> },
  ...(userRole !== 'bdTeam'
    ? [{ label: 'Pending approvals', sub: 'Contracts waiting for you', path: '/pendingApprovals', icon: <CheckSquareOutlined /> }]
    : []),
];

const SEARCH_DELAY_MS = 250;

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // search clients and projects on the server once the user stops typing
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setClients([]);
      setProjects([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [clientRes, projectRes] = await Promise.allSettled([
          clientsApi.getAllClientData([0, 1], 1, '', '', 'legalEntityName', q, undefined, { silent: true }),
          projectApi.getAllProjectData(1, '', '', 'projectName', q, { silent: true }),
        ]);
        if (cancelled) return;
        setClients(
          clientRes.status === 'fulfilled' ? clientRes.value.data.data.slice(0, 5) : []
        );
        setProjects(
          projectRes.status === 'fulfilled' ? projectRes.value.data.data.slice(0, 5) : []
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pages = PAGES.filter(
      (p) => !q || `${p.label} ${p.sub}`.toLowerCase().includes(q)
    );
    return [
      {
        title: 'Clients',
        rows: clients.map((c) => ({
          key: `c-${c.clientId}`,
          label: c.legalEntityName,
          sub: 'Client',
          path: `/clients/${c.clientId}`,
          icon: <BankOutlined />,
        })),
      },
      {
        title: 'Projects',
        rows: projects.map((p) => ({
          key: `p-${p.projectId}`,
          label: p.projectName,
          sub: p.legalEntityName || 'Project',
          path: `/projects/${p.projectId}`,
          icon: <ProjectOutlined />,
        })),
      },
      {
        title: 'Go to',
        rows: pages.map((p) => ({ ...p, key: `nav-${p.path}` })),
      },
    ].filter((g) => g.rows.length > 0);
  }, [clients, projects, query]);

  const flatRows = groups.flatMap((g) => g.rows);

  useEffect(() => {
    if (active >= flatRows.length) setActive(0);
  }, [flatRows.length, active]);

  const go = (row) => {
    if (!row) return;
    navigate(row.path);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (flatRows.length ? (i + 1) % flatRows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) =>
        flatRows.length ? (i - 1 + flatRows.length) % flatRows.length : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flatRows[active]);
    }
  };

  let rowIndex = -1;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={640}
      style={{ top: 96 }}
      className="cmdk"
      destroyOnClose
    >
      <div className="cmdk-input">
        <Input
          ref={inputRef}
          size="large"
          variant="borderless"
          prefix={<SearchOutlined style={{ color: '#475467' }} />}
          suffix={loading ? <Spin size="small" /> : <span className="kbd">Esc</span>}
          placeholder="Search clients, projects, or jump to a page…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          aria-label="Search"
        />
      </div>

      <div className="cmdk-results" role="listbox">
        {groups.map((group) => (
          <div key={group.title} className="cmdk-group">
            <div className="cmdk-group-title">{group.title}</div>
            {group.rows.map((row) => {
              rowIndex += 1;
              const index = rowIndex;
              return (
                <button
                  key={row.key}
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  className={`cmdk-row ${index === active ? 'is-active' : ''}`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(row)}
                >
                  <span className="cmdk-icon">{row.icon}</span>
                  <span className="cmdk-text">
                    <span className="cmdk-label">{row.label}</span>
                    <span className="cmdk-sub">{row.sub}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ))}
        {groups.length === 0 && !loading && (
          <div className="cmdk-empty">
            Nothing matches “{query}”. Try a client or project name.
          </div>
        )}
      </div>

      <div className="cmdk-footer">
        <span><b>↑ ↓</b> to move</span>
        <span><b>Enter</b> to open</span>
        <span><b>Esc</b> to close</span>
      </div>
    </Modal>
  );
};

export default CommandPalette;
